import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, Zap, TrendingUp } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { translations } from '../../translations';
import { submitAdaptiveSession } from '../../services/studioApi';
import UnifiedHeader from '../../components/UnifiedHeader';
import './Studio.css';

function ProgressBar({ current, total }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
      <div className="progress-bar-track" style={{ flex: 1 }}>
        <div className="progress-bar-fill" style={{ width: `${(current / total) * 100}%` }} />
      </div>
      <span style={{ fontSize: '0.82rem', color: '#888', whiteSpace: 'nowrap' }}>{current}/{total}</span>
    </div>
  );
}

export default function AdaptivePractice() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = (translations[language]?.studio) || {};
  const tStudent = t.student || {};

  // Session data is passed via location state from ConceptLearning
  const [session, setSession] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentIdx, setCurrentIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    // Session data comes from navigation state (set by ConceptLearning when starting)
    const state = window.history.state?.usr;
    if (state?.session) {
      setSession(state.session);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await submitAdaptiveSession(sessionId, { answers });
      setResults(res);
    } catch (e) {
      alert(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const setAnswer = (qId, val) => setAnswers(a => ({ ...a, [qId]: val }));

  if (!session) {
    return (
      <div className="studio-page">
        <UnifiedHeader subRow={
          <div className="studio-subheader-left">
            <button className="studio-back-btn" onClick={() => navigate(-1)}>
              <ArrowLeft size={15} /> {t.back || 'Back'}
            </button>
            <h1 className="studio-page-title">{tStudent.adaptivePractice || 'Adaptive Practice'}</h1>
          </div>
        } />
        <div className="studio-main" style={{ maxWidth: 480 }}>
          <div className="studio-card" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
            <p style={{ color: '#666', marginBottom: '1.5rem' }}>
              {tStudent.sessionNotFound || 'Session not found. Please start a new practice session.'}
            </p>
            <button className="btn-secondary" onClick={() => navigate(-1)}>
              {t.back || 'Back'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const questions = session.questions || session.generated_questions || [];

  // Results view
  if (results) {
    const pct = results.max_score > 0
      ? Math.round((results.total_score / results.max_score) * 100)
      : 0;
    const masteryDelta = results.mastery_updates
      ? Math.round((Object.values(results.mastery_updates)[0] || 0) * 100)
      : null;

    return (
      <div className="studio-page">
        <UnifiedHeader subRow={
          <div className="studio-subheader-left">
            <button className="studio-back-btn" onClick={() => navigate(-1)}>
              <ArrowLeft size={15} /> {t.back || 'Back'}
            </button>
            <h1 className="studio-page-title">
              {tStudent.adaptivePractice || 'Adaptive Practice'} — {tStudent.results || 'Results'}
            </h1>
          </div>
        } />
        <div className="studio-main" style={{ maxWidth: 640 }}>
          <div className="studio-card" style={{ textAlign: 'center', marginBottom: '1.5rem', padding: '2.5rem' }}>
            {pct >= 80 && <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🎉</div>}
            <div className={`score-circle ${pct >= 80 ? 'score-circle-great' : pct >= 50 ? 'score-circle-ok' : 'score-circle-low'}`}>
              <span className="score-circle-pct">{pct}%</span>
              <span className="score-circle-label">{results.total_score || 0}/{results.max_score || 0}</span>
            </div>
            <h2 style={{ margin: '0 0 0.4rem', color: '#1a1a2e' }}>
              {pct >= 80
                ? (tStudent.excellent || 'Excellent!')
                : pct >= 50
                  ? (tStudent.goodJob || 'Good work!')
                  : (tStudent.keepPracticing2 || 'Keep practicing!')}
            </h2>
            {masteryDelta !== null && masteryDelta > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', color: '#667eea', fontWeight: 600, fontSize: '0.9rem' }}>
                <TrendingUp size={16} />
                {tStudent.masteryUpdated || 'Mastery score updated!'} +{masteryDelta}%
              </div>
            )}
          </div>

          {/* Per-question feedback */}
          {results.ai_feedback && (
            <div className="studio-card">
              <h3 style={{ margin: '0 0 1rem', fontSize: '0.95rem', fontWeight: 700 }}>
                {tStudent.questionBreakdown || 'Question Breakdown'}
              </h3>
              {Object.entries(results.ai_feedback).map(([qId, fb], i) => (
                <div key={qId} style={{ padding: '0.75rem 0', borderBottom: '1px solid #f5f5f5' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                    {fb.correct || fb.score >= 0.7
                      ? <CheckCircle size={16} color="#4caf50" />
                      : <XCircle size={16} color="#f44336" />}
                    <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                      {tStudent.question || 'Q'}{i + 1}: {Math.round((fb.score || 0) * 100)}%
                    </span>
                  </div>
                  <p style={{ margin: '0.25rem 0 0.25rem 1.5rem', fontSize: '0.85rem', color: '#555' }}>{fb.feedback}</p>
                  {fb.gaps?.length > 0 && (
                    <div style={{ marginLeft: '1.5rem', display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                      {fb.gaps.map((g, j) => <span key={j} className="badge badge-red">{g}</span>)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            {pct < 80 && (
              <button className="btn-primary" onClick={() => { setResults(null); setAnswers({}); setCurrentIdx(0); }}>
                <Zap size={15} /> {tStudent.tryAgain || 'Try Again'}
              </button>
            )}
            <button className="btn-secondary" onClick={() => navigate(-1)}>
              {t.back || 'Back'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const q = questions[currentIdx];
  const isLast = currentIdx === questions.length - 1;
  const qOptions = q?.options
    ? (typeof q.options === 'string' ? JSON.parse(q.options) : q.options)
    : null;

  if (!q) {
    return (
      <div className="studio-page">
        <div className="loading-center"><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="studio-page">
      <UnifiedHeader subRow={
        <div className="studio-subheader-left">
          <h1 className="studio-page-title">{tStudent.adaptivePractice || 'Adaptive Practice'}</h1>
        </div>
      } />

      <div className="studio-main" style={{ maxWidth: 640 }}>
        {/* Intro banner */}
        <div className="studio-card" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', marginBottom: '1rem', padding: '1rem 1.25rem' }}>
          <p style={{ margin: 0, fontSize: '0.88rem', fontWeight: 500 }}>
            {tStudent.adaptiveIntro || 'Based on your results, here are targeted practice questions to strengthen your understanding.'}
          </p>
        </div>

        <ProgressBar current={currentIdx + 1} total={questions.length} />

        <div className="studio-card">
          <div style={{ marginBottom: '0.5rem' }}>
            <span className={`badge ${q.type === 'multiple_choice' ? 'badge-blue' : q.type === 'numeric' ? 'badge-orange' : 'badge-purple'}`}>
              {q.type}
            </span>
          </div>
          <h3 style={{ margin: '0.75rem 0 1.5rem', fontSize: '1.05rem', color: '#1a1a2e', lineHeight: 1.5 }}>
            {q.text}
          </h3>

          {q.type === 'multiple_choice' && qOptions && (
            <div className="answer-options">
              {qOptions.map((opt, i) => (
                <button key={i}
                  className={`answer-option ${answers[q.id] === opt ? 'selected' : ''}`}
                  onClick={() => setAnswer(q.id, opt)}
                >
                  <div className="answer-option-indicator" />
                  <span style={{ fontSize: '0.9rem', color: '#333' }}>{opt}</span>
                </button>
              ))}
            </div>
          )}

          {q.type === 'numeric' && (
            <input
              type="number"
              className="form-input"
              value={answers[q.id] || ''}
              onChange={e => setAnswer(q.id, e.target.value)}
              placeholder={tStudent.enterNumber || 'Enter your answer...'}
              style={{ fontSize: '1.1rem', textAlign: 'center' }}
            />
          )}

          {q.type === 'open_ended' && (
            <textarea
              className="form-textarea"
              rows={5}
              value={answers[q.id] || ''}
              onChange={e => setAnswer(q.id, e.target.value)}
              placeholder={tStudent.writeAnswer || 'Write your answer here...'}
            />
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
          <button className="btn-secondary" onClick={() => setCurrentIdx(i => Math.max(0, i - 1))} disabled={currentIdx === 0}>
            {tStudent.previous || 'Previous'}
          </button>
          {isLast ? (
            <button className="btn-primary" onClick={handleSubmit} disabled={submitting}>
              <CheckCircle size={16} /> {submitting ? (tStudent.submitting || 'Submitting...') : (tStudent.submit || 'Submit')}
            </button>
          ) : (
            <button className="btn-primary" onClick={() => setCurrentIdx(i => i + 1)}>
              {tStudent.next || 'Next'}
            </button>
          )}
        </div>

        {/* Question dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.4rem', marginTop: '1.25rem', flexWrap: 'wrap' }}>
          {questions.map((q2, i) => (
            <button key={i} onClick={() => setCurrentIdx(i)} style={{
              width: 28, height: 28, borderRadius: '50%', border: 'none', cursor: 'pointer',
              background: i === currentIdx ? '#667eea' : answers[q2.id] ? '#4caf50' : '#e8ecf0',
              color: i === currentIdx || answers[q2.id] ? 'white' : '#888',
              fontSize: '0.75rem', fontWeight: 700
            }}>{i + 1}</button>
          ))}
        </div>
      </div>
    </div>
  );
}
